/**
 * groupThreadTypingRepository — the store half of typing presence (T-R15,
 * Miles 2026-09-21).
 *
 * Store: `group_thread_messages/{threadId}/typing/{uid}` — one doc per typist,
 * carrying EXACTLY `{ uid, updatedAt, schemaVersion }` and nothing else. The
 * draft's text never leaves the browser, and neither does its length.
 *
 * It rides the RECEIPTS seam rather than the live-turn one, deliberately: a
 * receipt is gated at the COLLECTION (participant + a readable history floor)
 * with no per-doc floor, so a plain collection listener is provable for `list`
 * — no `where('orderTs','>=',floor)` bound to get right, because a typing row
 * is presence and has no place in history. `firestore.rules` pins the doc's
 * shape, pins `uid` to the writer, and pins `updatedAt == request.time`, so the
 * 4-second expiry is the SERVER's clock and a client cannot hold its own row
 * open by lying about when it typed.
 *
 * Both halves fail closed on the flag (`resolveTypingPresenceEnabled`), so with
 * it off no browser ever touches this path: the writer returns without a write
 * and the listener returns a no-op detach without attaching.
 *
 * Every write is best-effort. A refused publish (a thread whose registry
 * predates the floor backfill, a member whose add is still settling) is
 * swallowed: presence is a courtesy to the room, never a gate on anything the
 * person doing the typing can do.
 */
import { deleteDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { assertCurrentUid, collection, db, doc, serverTimestamp } from './firestoreUtils';
import { resolveTypingPresenceEnabled } from '../services/groupThreads/featureFlags';
import { GROUP_LISTENER_RETRY_DELAYS_MS } from './groupThreadMessagesRepository';
import {
  TYPING_COLLECTION,
  TYPING_SCHEMA_VERSION,
  fromTypingDoc,
  type TypingView,
} from '../services/groupThreads/typingPresence';

const GROUP_STORE = 'group_thread_messages';

export interface TypingRepositoryOptions {
  /** Resolve the account-scoped flags against this account instead of the mount-published one. */
  accountId?: string | null;
}

export function typingDocRef(threadId: string, uid: string) {
  return doc(db, GROUP_STORE, threadId, TYPING_COLLECTION, uid);
}

/**
 * Publish "I am typing" for the signed-in person. One small write; the caller
 * decides HOW OFTEN through `shouldPublishTyping`, so a burst of keystrokes is
 * one write rather than one per key.
 */
export async function publishTyping(
  threadId: string,
  typistUid: string,
  opts: TypingRepositoryOptions = {},
): Promise<void> {
  if (!resolveTypingPresenceEnabled(opts.accountId ? { accountId: opts.accountId } : {})) return;
  const id = (threadId ?? '').trim();
  const uid = (typistUid ?? '').trim();
  if (!id || !uid) return;
  // The caller passes the uid (the receipts writer's shape). Read the helper
  // before relying on it: `assertCurrentUid` swallows its own throw, so it
  // RECORDS a cross-uid attempt in the audit log and never blocks one — the wall
  // is firestore.rules, which pins the doc id to request.auth.uid and refuses a
  // write under anyone else's. A refused write is swallowed below like any other.
  assertCurrentUid(uid, 'publishTyping');
  try {
    await setDoc(typingDocRef(id, uid), {
      uid,
      updatedAt: serverTimestamp(),
      schemaVersion: TYPING_SCHEMA_VERSION,
    });
  } catch {
    /* best-effort: presence is a courtesy, never a gate */
  }
}

/**
 * Remove this person's row: after the idle window, on send, on leaving the
 * thread, and when the flag goes off under a live tab. Tolerant of a row that
 * is already gone — two of those paths can fire for the same keystroke.
 */
export async function clearTyping(
  threadId: string,
  typistUid: string,
  opts: TypingRepositoryOptions = {},
): Promise<void> {
  void opts;
  const id = (threadId ?? '').trim();
  const uid = (typistUid ?? '').trim();
  if (!id || !uid) return;
  assertCurrentUid(uid, 'clearTyping');
  // Deliberately NOT gated on the flag: if the lever is switched off while a
  // tab is live, that tab must still be able to take its own row down. A delete
  // the rules would refuse is swallowed like any other best-effort write.
  try {
    await deleteDoc(typingDocRef(id, uid));
  } catch {
    /* already gone, or refused — either way there is nothing to do */
  }
}

/**
 * Listen to who is typing in one thread. The retry ladder and the fail-closed
 * flag check are the shared-overlay listener's, so this behaves like every
 * other group listener under a rules denial or a dropped socket.
 *
 * Every snapshot is handed on, INCLUDING an empty one: "nobody is typing" is
 * information, and swallowing it would leave the last row on screen.
 */
export function subscribeThreadTyping(
  threadId: string,
  onTyping: (docs: TypingView[]) => void,
  opts: TypingRepositoryOptions = {},
): () => void {
  if (!resolveTypingPresenceEnabled(opts.accountId ? { accountId: opts.accountId } : {})) return () => {};
  const id = (threadId ?? '').trim();
  if (!id) return () => {};

  let stopped = false;
  let detachInner: (() => void) | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;

  const scheduleRetry = (): void => {
    if (stopped) return;
    const delay = GROUP_LISTENER_RETRY_DELAYS_MS[
      Math.min(retryIndex, GROUP_LISTENER_RETRY_DELAYS_MS.length - 1)
    ];
    retryIndex += 1;
    retryTimer = setTimeout(attach, delay);
  };

  const attach = (): void => {
    if (stopped) return;
    try {
      const unsubscribe = onSnapshot(
        collection(db, GROUP_STORE, id, TYPING_COLLECTION),
        (snap) => {
          retryIndex = 0;
          const rows: TypingView[] = [];
          snap.docs.forEach((d) => {
            const parsed = fromTypingDoc(d.id, d.data());
            if (parsed) rows.push(parsed);
          });
          onTyping(rows);
        },
        () => {
          try { unsubscribe(); } catch { /* already detached */ }
          scheduleRetry();
        },
      );
      detachInner = unsubscribe;
    } catch {
      scheduleRetry();
    }
  };

  attach();

  return () => {
    stopped = true;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    if (detachInner) {
      try { detachInner(); } catch { /* already detached */ }
      detachInner = null;
    }
  };
}
